import React from 'react';

interface PaginationProps {
  currentPage: number;          // 1-based
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;           // e.g. while loading
}

type PageItem = number | 'ellipsis-left' | 'ellipsis-right';

// 1 2 3 4 5 … 10 / 1 … 4 5 6 … 10 / 1 … 6 7 8 9 10
function buildPageItems(current: number, total: number): PageItem[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  if (current <= 4) {
    return [1, 2, 3, 4, 5, 'ellipsis-right', total];
  }
  if (current >= total - 3) {
    return [1, 'ellipsis-left', total - 4, total - 3, total - 2, total - 1, total];
  }
  return [1, 'ellipsis-left', current - 1, current, current + 1, 'ellipsis-right', total];
}

const baseBtn =
  'min-w-[32px] px-2.5 py-1 text-sm border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const idleBtn = 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100';
const activeBtn = 'border-blue-600 bg-blue-600 text-white font-semibold';

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
}) => {
  if (totalPages <= 1) return null;

  const go = (page: number) => {
    if (disabled) return;
    const target = Math.min(Math.max(1, page), totalPages);
    if (target !== currentPage) onPageChange(target);
  };

  return (
    <nav className="flex items-center gap-1 flex-wrap" aria-label="Pagination">
      <button
        type="button"
        className={`${baseBtn} ${idleBtn}`}
        onClick={() => go(currentPage - 1)}
        disabled={disabled || currentPage === 1}
      >
        Previous
      </button>

      {buildPageItems(currentPage, totalPages).map((item) =>
        typeof item === 'number' ? (
          <button
            key={item}
            type="button"
            className={`${baseBtn} ${item === currentPage ? activeBtn : idleBtn}`}
            onClick={() => go(item)}
            disabled={disabled && item !== currentPage}
            aria-current={item === currentPage ? 'page' : undefined}
          >
            {item}
          </button>
        ) : (
          <span key={item} className="px-1 text-sm text-gray-400 select-none">
            …
          </span>
        ),
      )}

      <button
        type="button"
        className={`${baseBtn} ${idleBtn}`}
        onClick={() => go(currentPage + 1)}
        disabled={disabled || currentPage === totalPages}
      >
        Next
      </button>
    </nav>
  );
};

export default Pagination;